import React, { Component } from "react";
import {
  Table,
  Message,
  Label,
  Segment,
  Header,
  Icon,
  Button,
} from "semantic-ui-react";
import { loanApi } from "@api/loans";
import { fineApi } from "@api/fine/fine";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import { documentApi } from "@api/documents";
import { calculateBusinessDaysLuxon } from "@utils/fineUtils";

class OverdueBooksTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      overdueBooks: [],
      loading: false,
      error: null,
    };
  }

  componentDidMount() {
    this.fetchOverdueBooks();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.fromDate !== this.props.fromDate ||
      prevProps.toDate !== this.props.toDate
    ) {
      this.fetchOverdueBooks();
    }
  }

  fetchOverdueBooks = async () => {
    const { fromDate, toDate } = this.props;
    this.setState({ loading: true, error: null });

    try {
      let query = loanApi
        .query()
        .withState(["ITEM_ON_LOAN"])
        .overdue()
        .sortByNewest()
        .withSize(1000);

      if (fromDate || toDate) {
        query = query.withStartDate({ fromDate, toDate });
      }

      const queryString = query.qs();
      const loansResponse = await loanApi.list(queryString);
      const overdueLoans = loansResponse.data.hits || [];

      const activeFinesResponse = await fineApi.getActiveFines();
      const activeFines = activeFinesResponse.data.hits.filter(
        (fine) => fine.status !== "resolved" && fine.status !== "cancelled"
      );

      const finesByLoanId = new Map();
      activeFines.forEach((fine) => {
        if (fine.loan_id) {
          finesByLoanId.set(fine.loan_id, fine);
        }
      });

      const overdueBooks = await Promise.all(
        overdueLoans.map(async (loan) => {
          const metadata = loan.metadata;
          const document = metadata.document || {};
          const item = metadata.item || {};
          const patron = metadata.patron || {};
          const dueDate = DateTime.fromISO(metadata.end_date);
          const today = DateTime.now();

          // Use centralized business days calculation
          const overdueDays = calculateBusinessDaysLuxon(dueDate, today);

          let completeDocument = document;
          if (document.pid || metadata.document_pid) {
            try {
              const documentPid = document.pid || metadata.document_pid;
              const documentResponse = await documentApi.get(documentPid);
              completeDocument = documentResponse.data.metadata;
            } catch (error) {
              console.warn(
                `Failed to fetch document details for ${document.pid}:`,
                error
              );
            }
          }

          // Extract author information
          const getAuthor = () => {
            if (
              completeDocument.authors &&
              typeof completeDocument.authors === "string"
            ) {
              return completeDocument.authors;
            }

            if (
              completeDocument.authors &&
              Array.isArray(completeDocument.authors) &&
              completeDocument.authors.length > 0
            ) {
              return (
                completeDocument.authors[0].full_name ||
                completeDocument.authors[0].name ||
                completeDocument.authors[0]
              );
            }

            if (completeDocument.author) {
              return typeof completeDocument.author === "string"
                ? completeDocument.author
                : completeDocument.author.name || "Unknown Author";
            }

            if (document.authors && typeof document.authors === "string") {
              return document.authors;
            }

            if (
              document.authors &&
              Array.isArray(document.authors) &&
              document.authors.length > 0
            ) {
              return (
                document.authors[0].full_name ||
                document.authors[0].name ||
                document.authors[0]
              );
            }

            if (document.author) {
              return typeof document.author === "string"
                ? document.author
                : document.author.name || "Unknown Author";
            }

            return "Unknown Author";
          };

          // Extract ISBN information
          const getISBN = () => {
            // Check for ISBN in complete document first
            if (completeDocument.isbn) {
              return Array.isArray(completeDocument.isbn)
                ? completeDocument.isbn[0]
                : completeDocument.isbn;
            }

            // Check for identifiers array with ISBN type
            if (
              completeDocument.identifiers &&
              Array.isArray(completeDocument.identifiers)
            ) {
              const isbnIdentifier = completeDocument.identifiers.find(
                (identifier) =>
                  identifier.scheme === "ISBN" ||
                  identifier.type === "ISBN" ||
                  identifier.scheme?.toLowerCase() === "isbn"
              );
              if (isbnIdentifier) {
                return isbnIdentifier.value || isbnIdentifier.identifier;
              }
            }

            // Check alternative_identifiers
            if (
              completeDocument.alternative_identifiers &&
              Array.isArray(completeDocument.alternative_identifiers)
            ) {
              const isbnIdentifier =
                completeDocument.alternative_identifiers.find(
                  (identifier) =>
                    identifier.scheme === "ISBN" ||
                    identifier.type === "ISBN" ||
                    identifier.scheme?.toLowerCase() === "isbn"
                );
              if (isbnIdentifier) {
                return isbnIdentifier.value || isbnIdentifier.identifier;
              }
            }

            // Fallback to document object
            if (document.isbn) {
              return Array.isArray(document.isbn)
                ? document.isbn[0]
                : document.isbn;
            }

            if (document.identifiers && Array.isArray(document.identifiers)) {
              const isbnIdentifier = document.identifiers.find(
                (identifier) =>
                  identifier.scheme === "ISBN" ||
                  identifier.type === "ISBN" ||
                  identifier.scheme?.toLowerCase() === "isbn"
              );
              if (isbnIdentifier) {
                return isbnIdentifier.value || isbnIdentifier.identifier;
              }
            }

            return null;
          };

          // Extract Call Number from complete document
          const getCallNumber = () => {
            if (completeDocument.call_number)
              return completeDocument.call_number;

            if (
              completeDocument.call_numbers &&
              Array.isArray(completeDocument.call_numbers) &&
              completeDocument.call_numbers.length > 0
            ) {
              return completeDocument.call_numbers[0];
            }

            if (
              completeDocument.sections &&
              Array.isArray(completeDocument.sections) &&
              completeDocument.sections.length > 0
            ) {
              return completeDocument.sections.join(", ");
            }

            if (item.call_number) return item.call_number;

            return null;
          };

          // Extract Item Barcode
          const getItemBarcode = () => {
            if (item.barcode) return item.barcode;

            if (metadata.item_barcode) return metadata.item_barcode;

            if (loan.item_barcode) return loan.item_barcode;

            return null;
          };

          const callNumber = getCallNumber();
          const isbn = getISBN();
          const itemBarcode = getItemBarcode();

          return {
            bookTitle:
              completeDocument.title || document.title || "Unknown Title",
            author: getAuthor(),
            isbn: isbn || null,
            callNumber: callNumber || null,
            itemBarcode: itemBarcode || null,
            borrowerName: patron.name || "Unknown Patron",
            borrowerContact: patron.email || "No email",
            borrowerPhone: patron.phone || null,
            dueDate: metadata.end_date,
            overdueDays: Math.max(0, overdueDays),
            loanId: loan.pid,
          };
        })
      );

      this.setState({ overdueBooks, loading: false });

      if (this.props.onDataChange) {
        this.props.onDataChange(overdueBooks);
      }
    } catch (error) {
      this.setState({
        error: `Failed to fetch overdue books: ${error.message}`,
        overdueBooks: [],
        loading: false,
      });
    }
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(amount);
  };

  formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED);
  };

  getSeverityColor = (overdueDays) => {
    return "red";
  };

  renderOverdueBooksTable() {
    const { overdueBooks } = this.state;

    if (!overdueBooks || overdueBooks.length === 0) {
      return (
        <Message info icon>
          <Icon name="info circle" />
          <Message.Content>
            <Message.Header>No Overdue Books Found</Message.Header>
            There are currently no overdue books for the selected date range.
          </Message.Content>
        </Message>
      );
    }

    return (
      <Table
        celled
        striped
        style={{
          fontSize: "14px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Table.Header>
          <Table.Row style={{ backgroundColor: "#f8f9fa" }}>
            <Table.HeaderCell
              style={{
                fontWeight: "600",
                color: "#2c3e50",
                padding: "16px",
                borderBottom: "2px solid #dee2e6",
              }}
            >
              Book Information
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{
                fontWeight: "600",
                color: "#2c3e50",
                padding: "16px",
                borderBottom: "2px solid #dee2e6",
                textAlign: "center",
              }}
            >
              Borrower Information
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{
                fontWeight: "600",
                color: "#2c3e50",
                padding: "16px",
                borderBottom: "2px solid #dee2e6",
                textAlign: "center",
              }}
            >
              Loan Details
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{
                fontWeight: "600",
                color: "#2c3e50",
                padding: "16px",
                borderBottom: "2px solid #dee2e6",
                textAlign: "center",
              }}
            >
              Days Overdue
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {overdueBooks.map((book, index) => (
            <React.Fragment key={index}>
              <Table.Row
                style={{
                  backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                }}
              >
                <Table.Cell
                  style={{
                    padding: "20px",
                    verticalAlign: "top",
                    borderRight: "1px solid #dee2e6",
                  }}
                >
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#2c3e50",
                      marginBottom: "8px",
                      lineHeight: "1.4",
                    }}
                  >
                    {book.bookTitle}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#6c757d",
                      marginBottom: "8px",
                    }}
                  >
                    <strong>Author:</strong> {book.author}
                  </div>

                  <div style={{ marginBottom: "4px" }}>
                    {/* ISBN display */}
                    {book.isbn && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6c757d",
                          backgroundColor: "#e7f3ff",
                          padding: "2px 6px",
                          borderRadius: "3px",
                          border: "1px solid #dee2e6",
                          display: "inline-block",
                          marginRight: "8px",
                          marginBottom: "4px",
                        }}
                      >
                        ISBN: {book.isbn}
                      </div>
                    )}

                    {book.callNumber && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6c757d",
                          backgroundColor: "#ffffff",
                          padding: "2px 6px",
                          borderRadius: "3px",
                          border: "1px solid #dee2e6",
                          display: "inline-block",
                          marginRight: "8px",
                          marginBottom: "4px",
                        }}
                      >
                        Call #: {book.callNumber}
                      </div>
                    )}

                    {/* Item Barcode display */}
                    {book.itemBarcode && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6c757d",
                          backgroundColor: "#fff",
                          padding: "2px 6px",
                          borderRadius: "3px",
                          border: "1px solid #dee2e6",
                          display: "inline-block",
                          marginRight: "8px",
                          marginBottom: "4px",
                        }}
                      >
                        Accession No.: {book.itemBarcode}
                      </div>
                    )}
                  </div>
                </Table.Cell>

                <Table.Cell
                  style={{
                    padding: "20px",
                    verticalAlign: "top",
                    borderRight: "1px solid #dee2e6",
                  }}
                >
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#2c3e50",
                      marginBottom: "8px",
                    }}
                  >
                    {book.borrowerName}
                  </div>
                  {book.borrowerContact &&
                    book.borrowerContact !== "No email" && (
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#6c757d",
                          marginBottom: "4px",
                        }}
                      >
                        {book.borrowerContact}
                      </div>
                    )}
                  {book.borrowerPhone && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6c757d",
                        backgroundColor: "#ffffff",
                        padding: "2px 6px",
                        borderRadius: "3px",
                        border: "1px solid #dee2e6",
                        display: "inline-block",
                      }}
                    >
                      <Icon name="phone" size="small" />
                      {book.borrowerPhone}
                    </div>
                  )}
                </Table.Cell>

                <Table.Cell
                  style={{
                    padding: "20px",
                    verticalAlign: "top",
                    borderRight: "1px solid #dee2e6",
                  }}
                >
                  <div
                    style={{
                      padding: "12px",
                      borderRadius: "6px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#2c3e50",
                        marginBottom: "6px",
                      }}
                    >
                      Due Date: {this.formatDate(book.dueDate)}
                    </div>
                    {book.loanId && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6c757d",
                          backgroundColor: "#ffffff",
                          padding: "2px 6px",
                          borderRadius: "3px",
                          border: "1px solid #dee2e6",
                          display: "inline-block",
                        }}
                      >
                        Loan ID: {book.loanId}
                      </div>
                    )}
                  </div>
                </Table.Cell>

                <Table.Cell
                  style={{
                    padding: "20px",
                    verticalAlign: "middle",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Label
                      size="large"
                      color={this.getSeverityColor(book.overdueDays)}
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        padding: "12px 16px",
                      }}
                    >
                      {book.overdueDays}
                      <Label.Detail>business days</Label.Detail>
                    </Label>
                  </div>
                </Table.Cell>
              </Table.Row>

              {index < overdueBooks.length - 1 && (
                <Table.Row>
                  <Table.Cell
                    colSpan="4"
                    style={{
                      padding: "0",
                      height: "12px",
                      backgroundColor: "#e9ecef",
                      borderTop: "2px solid #dee2e6",
                      borderBottom: "2px solid #dee2e6",
                    }}
                  />
                </Table.Row>
              )}
            </React.Fragment>
          ))}
        </Table.Body>
      </Table>
    );
  }

  exportToCSV = () => {
    const { overdueBooks } = this.state;
    if (!overdueBooks || overdueBooks.length === 0) return;

    const headers = [
      "Book Title",
      "Author",
      "ISBN",
      "Call Number",
      "Accession Number",
      "Borrower Name",
      "Borrower Email",
      "Borrower Phone",
      "Due Date",
      "Days Overdue (Business)",
      "Loan ID",
    ];

    const csvRows = overdueBooks.map((book) => {
      const row = [
        `"${(book.bookTitle || "").replace(/"/g, '""')}"`,
        `"${(book.author || "").replace(/"/g, '""')}"`,
        book.isbn || "",
        book.callNumber || "",
        book.itemBarcode || "",
        `"${(book.borrowerName || "").replace(/"/g, '""')}"`,
        book.borrowerContact || "",
        book.borrowerPhone || "",
        book.dueDate || "",
        book.overdueDays || "",
        book.loanId || "",
      ];
      return row.join(",");
    });

    const csvContent = [headers.join(","), ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `overdue_books_${DateTime.now().toISODate()}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  render() {
    const { loading, error } = this.state;

    if (error) {
      return (
        <Segment>
          <Message negative icon>
            <Icon name="exclamation triangle" />
            <Message.Content>
              <Message.Header>Error Loading Data</Message.Header>
              <p>{error}</p>
              <Button
                icon="refresh"
                content="Retry"
                color="red"
                onClick={this.fetchOverdueBooks}
              />
            </Message.Content>
          </Message>
        </Segment>
      );
    }

    return (
      <Segment loading={loading}>
        <Header as="h3" dividing>
          <Icon name="book" color="green" />
          <Header.Content>
            Overdue Books Report
            <Header.Subheader style={{ fontSize: "0.7em", color: "#666" }}>
              Complete list of all overdue books with borrower information.
            </Header.Subheader>
          </Header.Content>
        </Header>
        {this.renderOverdueBooksTable()}
      </Segment>
    );
  }
}

OverdueBooksTab.propTypes = {
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  onDataChange: PropTypes.func,
};

OverdueBooksTab.defaultProps = {
  fromDate: "",
  toDate: "",
  onDataChange: null,
};

export default OverdueBooksTab;
